import { z } from 'zod';
import { ALLOWED_FONTS, MENU_ITEM_KEYS, MENU_STYLES, THEME_MODES, REMINDER_LEAD_MIN_MINUTES, REMINDER_LEAD_MAX_MINUTES, PASSENGER_LOCALES, CUSTOMER_FEATURE_KEYS, } from '../constants.js';
import { currencySchema, hexColorSchema, ianaTimezoneSchema, emailSchema } from './common.js';
export const menuItemSchema = z.object({
    key: z.enum(MENU_ITEM_KEYS),
    label: z.string().trim().min(1).max(24),
    visible: z.boolean(),
});
/**
 * A customer-app feature an operator has switched on or off.
 *
 * Same shape as a menu item on purpose: the operator edits both on the same screen, and
 * one of them being ordered-list-with-a-toggle while the other was a bag of booleans
 * would be two mental models for one job. Order decides the sequence of the actions on a
 * trip card, so an operator can lead with what their passengers actually use.
 *
 * There is deliberately no per-device flag. This is operator-level view customisation;
 * whether a control fits a narrow screen is the app's problem, not a setting.
 */
export const customerFeatureSchema = z.object({
    key: z.enum(CUSTOMER_FEATURE_KEYS),
    label: z.string().trim().min(1).max(24),
    visible: z.boolean(),
});
export const brandingColorsSchema = z.object({
    primary: hexColorSchema,
    secondary: hexColorSchema,
    background: hexColorSchema,
    surface: hexColorSchema,
    text: hexColorSchema,
});
/**
 * Body of PUT /admin/branding — what the OPERATOR owns (DECISIONS D69).
 *
 * Their identity: what the app is called, its colours, its typeface, light or dark. Which
 * pages exist and which capabilities are switched on is not here; that is the shape of the
 * product being sold, and it moved to the platform console.
 *
 * Zod strips unknown keys rather than rejecting them, so a tenant-admin build that still
 * posts menuItems keeps saving successfully and simply has no effect on them — which is
 * what stops this change from breaking a console mid-deploy.
 */
export const brandingUpdateSchema = z.object({
    appName: z.string().trim().min(2).max(20),
    colors: brandingColorsSchema,
    fontFamily: z.enum(ALLOWED_FONTS),
    themeMode: z.enum(THEME_MODES),
});
/**
 * Body of PUT /platform/tenants/:id/experience — what the PLATFORM owns (DECISIONS D69).
 *
 * Navigation shape, which pages a tenant's passengers get, and which customer features are
 * switched on. This is the difference between one plan and another, so it is sold rather
 * than self-served: an operator cannot switch on a capability they are not paying for.
 */
export const tenantExperienceSchema = z.object({
    menuStyle: z.enum(MENU_STYLES),
    menuItems: z
        .array(menuItemSchema)
        .min(1)
        .max(MENU_ITEM_KEYS.length)
        .refine((items) => new Set(items.map((i) => i.key)).size === items.length, 'Menu item keys must be unique')
        .refine((items) => items.some((i) => i.visible), 'At least one menu item must be visible'),
    /**
     * Optional: absent means "leave as is". No "at least one visible" refine, unlike
     * menuItems — a menu with nothing in it is a broken app, whereas a plan with none of
     * these extras is a plan.
     */
    customerFeatures: z
        .array(customerFeatureSchema)
        .max(CUSTOMER_FEATURE_KEYS.length)
        .refine((items) => new Set(items.map((i) => i.key)).size === items.length, 'Customer feature keys must be unique')
        .optional(),
});
export const tenantSettingsSchema = z.object({
    // '' is a real value: it means "no operator default, follow the browser".
    defaultLocale: z.union([z.enum(PASSENGER_LOCALES), z.literal('')]),
    reminderLeadMinutes: z
        .number()
        .int()
        .min(REMINDER_LEAD_MIN_MINUTES)
        .max(REMINDER_LEAD_MAX_MINUTES),
    paymentWindowMinutes: z.number().int().min(5).max(120),
    cancellationCutoffHours: z.number().int().min(0).max(720),
    maxSeatsPerBooking: z.number().int().min(1).max(20),
    supportEmail: z.union([emailSchema, z.literal('')]),
    supportPhone: z.string().trim().max(32),
    aboutHtml: z.string().max(20_000),
    faqHtml: z.string().max(20_000),
    /** Operator identity, printed on the ticket. See DEFAULT_TENANT_SETTINGS. */
    companyLegalName: z.string().trim().max(120),
    registrationNumber: z.string().trim().max(64),
    taxId: z.string().trim().max(64),
    companyAddress: z.string().trim().max(200),
});
/** PUT /admin/settings — timezone/currency are separately guarded (currency locks). */
export const tenantSettingsUpdateSchema = tenantSettingsSchema.partial().extend({
    timezone: ianaTimezoneSchema.optional(),
    currency: currencySchema.optional(),
});
//# sourceMappingURL=tenant.js.map